import { useEffect, useState } from 'react';

import { Button, Drawer } from '@/design-system/primitives';
import { cn } from '@/lib/cn';
import type {
  MarketplaceFilterOption,
  MarketplaceFilters as MarketplaceFiltersState,
} from '../marketplaceFilters';
import {
  EMPTY_MARKETPLACE_FILTERS,
  marketplaceFilterCount,
  toggleMarketplaceFilter,
} from '../marketplaceFilters';

interface MarketplaceFilterFieldsProps {
  filters: MarketplaceFiltersState;
  needOptions: MarketplaceFilterOption[];
  methodOptions: MarketplaceFilterOption[];
  onChange: (filters: MarketplaceFiltersState) => void;
  idPrefix: string;
}

interface MarketplaceFiltersDrawerProps {
  open: boolean;
  filters: MarketplaceFiltersState;
  needOptions: MarketplaceFilterOption[];
  methodOptions: MarketplaceFilterOption[];
  onClose: () => void;
  onApply: (filters: MarketplaceFiltersState) => void;
  getResultCount: (filters: MarketplaceFiltersState) => number;
}

export function MarketplaceFiltersPanel({
  filters,
  needOptions,
  methodOptions,
  onChange,
}: Omit<MarketplaceFilterFieldsProps, 'idPrefix'>) {
  const activeCount = marketplaceFilterCount(filters);

  return (
    <aside
      aria-label="Product filters"
      className="hidden lg:block sticky top-15 border border-line-subtle bg-raised"
    >
      <div className="h-50 px-15 border-b border-line-subtle flex items-center justify-between">
        <h2 className="type-label-md">Filters</h2>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={() => onChange(EMPTY_MARKETPLACE_FILTERS)}
            className="min-h-32 px-5 type-label-xs text-ink-muted underline underline-offset-4 hover:text-ink focus-visible:outline-none focus-visible:shadow-focus"
          >
            Clear all
          </button>
        )}
      </div>
      <MarketplaceFilterFields
        filters={filters}
        needOptions={needOptions}
        methodOptions={methodOptions}
        onChange={onChange}
        idPrefix="shop-desktop"
      />
    </aside>
  );
}

export function MarketplaceFiltersDrawer({
  open,
  filters,
  needOptions,
  methodOptions,
  onClose,
  onApply,
  getResultCount,
}: MarketplaceFiltersDrawerProps) {
  const [draftFilters, setDraftFilters] =
    useState<MarketplaceFiltersState>(filters);

  useEffect(() => {
    if (open) {
      setDraftFilters({
        needs: [...filters.needs],
        methods: [...filters.methods],
      });
    }
  }, [filters, open]);

  const resultCount = getResultCount(draftFilters);
  const activeCount = marketplaceFilterCount(draftFilters);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Filters"
      ariaLabel="Filter Shop products"
      footer={
        <div className="border-t border-line-subtle bg-page p-15 pb-20">
          <Button
            type="button"
            className="w-full h-50"
            onClick={() => onApply(draftFilters)}
          >
            Show {resultCount} {resultCount === 1 ? 'treatment' : 'treatments'}
          </Button>
        </div>
      }
    >
      <div className="flex items-center justify-end min-h-40 px-15 border-b border-line-subtle">
        {activeCount > 0 && (
          <button
            type="button"
            onClick={() => setDraftFilters(EMPTY_MARKETPLACE_FILTERS)}
            className="min-h-32 px-5 type-label-xs text-ink-muted underline underline-offset-4 hover:text-ink focus-visible:outline-none focus-visible:shadow-focus"
          >
            Clear all
          </button>
        )}
      </div>
      <MarketplaceFilterFields
        filters={draftFilters}
        needOptions={needOptions}
        methodOptions={methodOptions}
        onChange={setDraftFilters}
        idPrefix="shop-mobile"
      />
    </Drawer>
  );
}

function MarketplaceFilterFields({
  filters,
  needOptions,
  methodOptions,
  onChange,
  idPrefix,
}: MarketplaceFilterFieldsProps) {
  return (
    <div className="px-15 py-20">
      <FilterGroup
        label="By need"
        options={needOptions}
        selected={filters.needs}
        idPrefix={`${idPrefix}-need`}
        onToggle={(value) =>
          onChange({
            ...filters,
            needs: toggleMarketplaceFilter(filters.needs, value),
          })
        }
      />
      <div className="my-20 border-t border-line-subtle" />
      <FilterGroup
        label="Treatment type"
        options={methodOptions}
        selected={filters.methods}
        idPrefix={`${idPrefix}-method`}
        onToggle={(value) =>
          onChange({
            ...filters,
            methods: toggleMarketplaceFilter(filters.methods, value),
          })
        }
      />
    </div>
  );
}

function FilterGroup({
  label,
  options,
  selected,
  onToggle,
  idPrefix,
}: {
  label: string;
  options: MarketplaceFilterOption[];
  selected: string[];
  onToggle: (value: string) => void;
  idPrefix: string;
}) {
  return (
    <fieldset>
      <legend className="type-label-xs text-ink-muted mb-12">{label}</legend>
      <div className="flex flex-col gap-4">
        {options.map((option) => {
          const checked = selected.includes(option.value);
          const id = `${idPrefix}-${option.value}`;
          return (
            <label
              key={option.value}
              htmlFor={id}
              className={cn(
                'min-h-40 -mx-8 px-8 flex items-center gap-10 cursor-pointer',
                'hover:bg-sunken focus-within:shadow-focus',
              )}
            >
              <input
                id={id}
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(option.value)}
                className="h-16 w-16 shrink-0 accent-accent focus-visible:outline-none"
              />
              <span className="type-body-md flex-1">{option.label}</span>
              <span
                aria-hidden
                className="type-data-sm text-ink-muted tabular-nums"
              >
                {option.count}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
